/**
 * Appointment data models with Zod schemas
 * Single source of truth pattern: Base schemas with derived variants
 */
import { formatISO, parseISO } from "date-fns";
import { z } from "zod";
import {
  AppointmentServiceDtoSchema,
  AppointmentServiceSchema,
  CreateAppointmentServiceDtoSchema,
  CreateAppointmentServiceRequestSchema,
  fromAppointmentServiceDtoToAppointmentService,
  fromCreateAppointmentServiceRequestToCreateAppointmentServiceDto,
} from "./appointmentServiceTypes";

export const AppointmentStatusSchema = z.enum([
  "pending", "confirmed", "completed", "cancelled", "declined", "no_show",
]);
export type AppointmentStatus = z.infer<typeof AppointmentStatusSchema>;

export const AppointmentSchema = z.object({
  id: z.string(),
  revision: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
  createdAt: z.date(),
  modifiedAt: z.date().nullable(),
  businessId: z.string().nullable(),
  locationId: z.string().nullable(),
  clientId: z.string().nullable(),
  startAt: z.date().nullable(),
  endAt: z.date().nullable(),
  status: AppointmentStatusSchema,
  source: z.string().nullable(),
  price: z.number().nullable(),
  depositAmount: z.number().nullable(),
  notes: z.string().nullable(),
  confirmedAt: z.date().nullable(),
  cancelledAt: z.date().nullable(),
  createdBy: z.string().nullable(),
  services: z.array(AppointmentServiceSchema),
});

const CreateAppointmentRequestSchema = AppointmentSchema.omit({
  id: true,
  revision: true,
  createdAt: true,
  cancelledAt: true,
  confirmedAt: true,
  modifiedAt: true,
  createdBy: true,
  services: true,
})
  .extend({
    status: z.enum(["pending", "confirmed"]),
    services: z.array(CreateAppointmentServiceRequestSchema).optional(),
  })
  .partial({
    businessId: true,
    locationId: true,
    clientId: true,
    startAt: true,
    endAt: true,
    status: true,
    source: true,
    price: true,
    depositAmount: true,
    notes: true,
    services: true,
  });

const UpdateAppointmentRequestSchema = AppointmentSchema.omit({
  id: true,
  revision: true,
  createdAt: true,
  businessId: true,
  createdBy: true,
  services: true,
  confirmedAt: true,
  cancelledAt: true,
  modifiedAt: true,
})
  .extend({
    services: z.array(CreateAppointmentServiceRequestSchema).optional(),
  })
  .partial();

export const AppointmentDtoSchema = z
  .object({
    id: z.string(),
    revision: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
    created_at: z.string(),
    modified_at: z.string().nullable(),
    business_id: z.string().nullable(),
    location_id: z.string().nullable(),
    client_id: z.string().nullable(),
    start_at: z.string().nullable(),
    end_at: z.string().nullable(),
    status: AppointmentStatusSchema,
    source: z.string().nullable(),
    price: z.number().nullable(),
    deposit_amount: z.number().nullable(),
    notes: z.string().nullable(),
    confirmed_at: z.string().nullable(),
    cancelled_at: z.string().nullable(),
    created_by: z.string().nullable(),
  })
  .extend({
    services: z.array(AppointmentServiceDtoSchema).optional(),
  });

// Derived DTO schemas - automatically stay in sync with base
export const CreateAppointmentDtoSchema = AppointmentDtoSchema.omit({
  id: true,
  revision: true,
  created_at: true,
  modified_at: true,
  created_by: true,
  confirmed_at: true,
  cancelled_at: true,
})
  .extend({
    status: z.enum(["pending", "confirmed"]),
    services: z.array(CreateAppointmentServiceDtoSchema).optional(),
  })
  .partial();

export const UpdateAppointmentDtoSchema = AppointmentDtoSchema.omit({
  id: true,
  revision: true,
  created_at: true,
  modified_at: true,
  business_id: true,
  created_by: true,
  confirmed_at: true,
  cancelled_at: true,
})
  .extend({
    services: z.array(CreateAppointmentServiceDtoSchema).optional(),
  })
  .partial();

// Infer DTO types
export type AppointmentDto = z.infer<typeof AppointmentDtoSchema>;
export type CreateAppointmentDto = z.infer<typeof CreateAppointmentDtoSchema>;
export type UpdateAppointmentDto = z.infer<typeof UpdateAppointmentDtoSchema>;

// Infer domain type
export type Appointment = z.infer<typeof AppointmentSchema>;
export type CreateAppointmentRequest = z.infer<
  typeof CreateAppointmentRequestSchema
>;
export type UpdateAppointmentRequest = z.infer<
  typeof UpdateAppointmentRequestSchema
>;

// ============================================================================
// CONVERSION UTILITIES
// ============================================================================

export function fromAppointmentToRebookingRequest(
  appointment: Appointment
): CreateAppointmentRequest {
  const original = AppointmentSchema.parse(appointment);
  return {
    businessId: original.businessId,
    locationId: original.locationId,
    clientId: original.clientId,
    status: "confirmed",
    price: original.price,
    depositAmount: original.depositAmount,
    notes: original.notes,
    services: [...original.services]
      .sort((a, b) => (a.sequenceOrder ?? 0) - (b.sequenceOrder ?? 0))
      .map((line) => ({
        serviceId: line.serviceId,
        price: line.price,
        durationMinutes: line.durationMinutes,
        sequenceOrder: line.sequenceOrder,
        notes: line.notes,
      })),
  };
}

export function fromAppointmentDtoToAppointment(
  dto: AppointmentDto
): Appointment {
  const appointmentDto = AppointmentDtoSchema.parse(dto);

  return {
    id: appointmentDto.id,
    revision: appointmentDto.revision,
    createdAt: parseISO(appointmentDto.created_at),
    modifiedAt: appointmentDto.modified_at
      ? parseISO(appointmentDto.modified_at)
      : null,
    businessId: appointmentDto.business_id,
    locationId: appointmentDto.location_id,
    clientId: appointmentDto.client_id,
    startAt: appointmentDto.start_at ? parseISO(appointmentDto.start_at) : null,
    endAt: appointmentDto.end_at ? parseISO(appointmentDto.end_at) : null,
    status: appointmentDto.status,
    source: appointmentDto.source,
    price: appointmentDto.price,
    depositAmount: appointmentDto.deposit_amount,
    notes: appointmentDto.notes,
    confirmedAt: appointmentDto.confirmed_at
      ? parseISO(appointmentDto.confirmed_at)
      : null,
    cancelledAt: appointmentDto.cancelled_at
      ? parseISO(appointmentDto.cancelled_at)
      : null,
    createdBy: appointmentDto.created_by,
    services: appointmentDto.services
      ? appointmentDto.services.map(
          fromAppointmentServiceDtoToAppointmentService
        )
      : [],
  };
}

export function fromCreateAppointmentRequestToCreateAppointmentDto(
  request: CreateAppointmentRequest
): CreateAppointmentDto {
  const createAppointmentRequest =
    CreateAppointmentRequestSchema.parse(request);

  return {
    notes: createAppointmentRequest.notes,
    status: createAppointmentRequest.status,
    source: createAppointmentRequest.source,
    price: createAppointmentRequest.price,
    business_id: createAppointmentRequest.businessId,
    location_id: createAppointmentRequest.locationId,
    client_id: createAppointmentRequest.clientId,
    start_at: createAppointmentRequest.startAt
      ? formatISO(createAppointmentRequest.startAt)
      : undefined,
    end_at: createAppointmentRequest.endAt
      ? formatISO(createAppointmentRequest.endAt)
      : undefined,
    deposit_amount: createAppointmentRequest.depositAmount,
    services: createAppointmentRequest.services
      ? createAppointmentRequest.services.map((service) =>
          fromCreateAppointmentServiceRequestToCreateAppointmentServiceDto(
            service
          )
        )
      : [],
  };
}

export function fromUpdateAppointmentRequestToUpdateAppointmentDto(
  request: UpdateAppointmentRequest
): UpdateAppointmentDto {
  const updateAppointmentRequest =
    UpdateAppointmentRequestSchema.parse(request);
  return {
    notes: updateAppointmentRequest.notes,
    status: updateAppointmentRequest.status,
    source: updateAppointmentRequest.source,
    price: updateAppointmentRequest.price,
    location_id: updateAppointmentRequest.locationId,
    client_id: updateAppointmentRequest.clientId,
    start_at: updateAppointmentRequest.startAt
      ? formatISO(updateAppointmentRequest.startAt)
      : undefined,
    end_at: updateAppointmentRequest.endAt
      ? formatISO(updateAppointmentRequest.endAt)
      : undefined,
    deposit_amount: updateAppointmentRequest.depositAmount,
    services: updateAppointmentRequest.services
      ? updateAppointmentRequest.services.map((service) =>
          fromCreateAppointmentServiceRequestToCreateAppointmentServiceDto(
            service
          )
        )
      : [],
  };
}
